import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion } from 'framer-motion';
import styles from './styles.module.scss';

const ANIMATION_DURATION = 0.5;

// Plus icon component
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 1V13M1 7H13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Minus icon component
const MinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 7H13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FeaturesAccordionMobile = ({ items = [] }) => {
  const [expandedItemId, setExpandedItemId] = useState(null);
  const itemRefs = useRef({});

  const handleItemClick = (itemId) => {
    const wasExpanded = expandedItemId === itemId;
    const newExpandedItemId = wasExpanded ? null : itemId;
    setExpandedItemId(newExpandedItemId);

    // Scroll into view when expanding
    if (!wasExpanded && itemRefs.current[itemId]) {
      setTimeout(() => {
        const element = itemRefs.current[itemId];
        if (element) {
          const buttonElement = element.querySelector('button');
          if (buttonElement) {
            const buttonPosition = buttonElement.getBoundingClientRect().top;
            const offsetPosition = buttonPosition + window.pageYOffset - 80;
            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth',
            });
          }
        }
      }, ANIMATION_DURATION * 1000 + 50);
    }
  };

  useEffect(() => {
    if (items && items.length > 0) {
      setExpandedItemId(items[0]?.image?.id);
      items.forEach((item) => {
        const imageDiv = document.getElementById(`img-box-${item?.image?.id}`);
        const image = document.getElementById(`img-${item?.image?.id}`);
        if (imageDiv && image) {
          imageDiv.innerHTML = '';
          imageDiv.appendChild(image.cloneNode(true));
        }
      });
    }
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={styles.itemsList}>
      {items.map((item) => {
        const isExpanded = expandedItemId === item?.image?.id;

        return (
          <div
            key={item?.image?.id}
            ref={(el) => {
              if (el) {
                itemRefs.current[item?.image?.id] = el;
              }
            }}
            className={cn(styles.item, {
              [styles.expanded]: isExpanded,
            })}
          >
            <button
              type="button"
              className={cn(styles.itemButton, { [styles.expanded]: isExpanded })}
              onClick={() => handleItemClick(item?.image?.id)}
            >
              <div className={styles.itemHeader}>
                <div className={styles.itemTitle}>{item.title}</div>
                <span className={styles.expandIcon}>
                  {isExpanded ? <MinusIcon /> : <PlusIcon />}
                </span>
              </div>
            </button>

            <motion.div
              className={styles.content}
              initial="hidden"
              animate={isExpanded ? 'visible' : 'hidden'}
              variants={{
                hidden: {
                  height: 0,
                  opacity: 0,
                  marginTop: 0,
                },
                visible: {
                  height: 'auto',
                  opacity: 1,
                },
              }}
              transition={{
                duration: ANIMATION_DURATION,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <div className={styles.contentInner}>
                {item.image && (
                  <motion.div
                    id={`img-box-${item?.image?.id}`}
                    className={styles.imageBox}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isExpanded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                    transition={{ delay: 0.1, duration: ANIMATION_DURATION }}
                  />
                )}
                {item.description && (
                  <motion.p
                    className={styles.desc}
                    initial={{ opacity: 0 }}
                    animate={isExpanded ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ delay: 0.15, duration: ANIMATION_DURATION }}
                  >
                    {item.description}
                  </motion.p>
                )}
              </div>
            </motion.div>
          </div>
        );
      })}
    </div>
  );
};


FeaturesAccordionMobile.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.number,
      }),
    })
  ),
};

export default FeaturesAccordionMobile;
